import { SummaryResourceConcernComponent } from '@cibo/core'
import { ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  AccordionDetails,
  AccordionSummary,
  Card,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  useTheme,
} from '@mui/material'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecommendationsSummary, useResourceConcernsDefinition } from '../../queries'

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(() => ({
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}))

export const PracticeSelectionSummaryTable = ({
  resourceIds,
  engagementId,
}: Partial<Pick<ResourceDetailFeatureTaskEditorProps, 'resourceIds' | 'engagementId'>>) => {
  const { t } = useTranslation('@cibo/landmanager/PracticeSelectionConfirmation')
  const { palette } = useTheme()
  const {
    data: recommendationsSummary,
    isLoading,
    dataUpdatedAt,
  } = useRecommendationsSummary({
    engagementId,
  })

  const resourceConcernComponents =
    recommendationsSummary?.recommendationSummary.resourceConcernComponents
  const {
    data: resourceConcernsDefinitions,
    isLoading: resourceConcernLoading,
    dataUpdatedAt: resourceConcernContentUpdatedAt,
  } = useResourceConcernsDefinition()

  const resourceConcernDefinitions = resourceConcernsDefinitions?.items

  const resourceConcernList = useMemo(() => {
    return (
      recommendationsSummary &&
      resourceConcernComponents?.map((component: SummaryResourceConcernComponent) => {
        const matchingResourceConcern = resourceConcernDefinitions?.find(
          (definition: { fields: { resourceConcernComponentId: number } }) =>
            definition.fields.resourceConcernComponentId === component.resourceConcernId
        )
        return {
          ...component,
          definition: matchingResourceConcern ? matchingResourceConcern.fields.definition : '',
        }
      })
    )
  }, [dataUpdatedAt, resourceConcernContentUpdatedAt])

  const [expanded, setExpanded] = useState(true)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  return (
    <Stack padding={3}>
      <Accordion expanded={expanded} onChange={handleExpandClick}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-label={t('expand')}>
          <Typography variant="h6" component="h2">
            {t('resourceConcernToAddress')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack>
            {isLoading || resourceConcernLoading ? (
              <Grid columns={{ xs: 4, md: 8, lg: 12 }} container>
                <Grid xs={12} item>
                  <Skeleton variant="rounded" height={150} />
                </Grid>
              </Grid>
            ) : (
              <Stack>
                <TableContainer
                  component={Card}
                  elevation={0}
                  color={palette.text.primary}
                  sx={{
                    '& .MuiTableHead-root': {
                      backgroundColor: 'transparent',
                      color: `${palette.text.primary} !important`,
                    },
                    '& .MuiTableCell-root': {
                      color: palette.text.primary,
                    },
                  }}
                >
                  <Table>
                    <TableHead sx={{ '.MuiTableCell-root': { color: 'text.primary' } }}>
                      <TableRow>
                        <TableCell>{t('growersResourceConcerns')}</TableCell>
                        <TableCell>{t('fieldsAddressed')}</TableCell>
                        <TableCell>{t('statePriority')}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {resourceConcernList?.map(row => (
                        <TableRow key={row.resourceConcernId}>
                          <TableCell>
                            <Typography variant="body1" gutterBottom>
                              {row.resourceConcernDesc}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {row.definition}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {t('aOfB', {
                              countA: row.numFieldsAddressed,
                              countB: row.numFieldsWithConcern,
                            })}
                          </TableCell>
                          <TableCell align="center">
                            {row.currentState === 15 ? <CheckIcon color="success" /> : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  )
}
